import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { DashboardCard } from '../components/DashboardCard'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'

// Mock payment data
const mockPaymentData = {
  student: {
    name: 'น้องใส',
    student_code: 'STU-001'
  },
  pending_payments: [
    {
      id: 'INV-2568-09-001',
      month: 'กันยายน 2568',
      amount: 1600,
      due_date: '2025-09-01',
      status: 'unpaid' as const,
      invoice_date: '2025-08-25'
    }
  ],
  payment_history: [
    {
      id: 'INV-2568-08-001',
      month: 'สิงหาคม 2568',
      amount: 1600,
      paid_date: '2025-08-01',
      status: 'paid' as const,
      reference: 'KBANK001'
    },
    {
      id: 'INV-2568-07-001',
      month: 'กรกฎาคม 2568',
      amount: 1600,
      paid_date: '2025-07-01',
      status: 'paid' as const,
      reference: 'KBANK002'
    }
  ],
  bank_info: {
    bank_name: 'ธนาคารกสิกรไทย',
    account_name: 'โรงเรียนกวดวิชาครูดรีม',
    account_number: '123-4-56789-0',
    promptpay_id: '0812345678'
  }
}

export const PaymentPage: React.FC = () => {
  useAuthStore() // Access to auth state
  const navigate = useNavigate()
  
  // Utility functions
  const formatThaiDate = (dateString: string) => {
    const date = new Date(dateString)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }
  
  const formatThaiBaht = (amount: number) => {
    return `฿${new Intl.NumberFormat('th-TH').format(amount)}`
  }

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date()
    const due = new Date(dueDate)
    const diffTime = due.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const handlePaymentClick = (invoiceId: string) => {
    navigate(`/payment-slip/${invoiceId}`)
  }

  const handleShowInvoice = (invoiceId: string) => {
    navigate(`/invoice/${invoiceId}`)
  }


  const handleQuickUpload = () => {
    if (mockPaymentData.pending_payments.length > 0) {
      navigate(`/payment-slip/${mockPaymentData.pending_payments[0].id}`)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Page Header */}
      <PageHeader 
        title="ชำระค่าเรียน"
        subtitle={`นักเรียน: ${mockPaymentData.student.name}`}
      />
      
      {/* Main Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Pending Payments */}
        {mockPaymentData.pending_payments.length > 0 && (
          <DashboardCard
            title="🔴 ค่าเรียนที่ต้องชำระ"
            className="bg-gradient-to-r from-red-50 to-pink-50 border-red-200"
          >
            <div className="space-y-4">
              {mockPaymentData.pending_payments.map((payment) => {
                const daysUntilDue = getDaysUntilDue(payment.due_date)
                const isOverdue = daysUntilDue < 0
                const isUrgent = daysUntilDue <= 3 && daysUntilDue >= 0
                
                return (
                  <div key={payment.id} className="bg-white rounded-lg border p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{payment.month}</h3>
                        <p className="text-sm text-gray-600">เลขที่: {payment.id}</p>
                        <p className="text-sm text-gray-500">
                          ออกใบแจ้งหนี้: {formatThaiDate(payment.invoice_date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900">
                          {formatThaiBaht(payment.amount)}
                        </p>
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          isOverdue 
                            ? 'bg-red-100 text-red-800' 
                            : isUrgent 
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {isOverdue 
                            ? `เกินกำหนด ${Math.abs(daysUntilDue)} วัน`
                            : isUrgent
                            ? `เหลือ ${daysUntilDue} วัน`
                            : `ครบกำหนด: ${formatThaiDate(payment.due_date)}`
                          }
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handlePaymentClick(payment.id)}
                        className="btn-primary flex-1"
                      >
                        ชำระเงิน
                      </button>
                      <button 
                        onClick={() => handleShowInvoice(payment.id)}
                        className="btn-secondary"
                      >
                        ดูใบแจ้งหนี้
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </DashboardCard>
        )}

        {/* Payment Instructions */}
        <DashboardCard
          title="💳 วิธีการชำระเงิน"
          className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
        >
          <div className="space-y-4">
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-semibold text-gray-900 mb-3">ข้อมูลบัญชีธนาคาร</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">ธนาคาร:</span>
                  <span className="font-medium">{mockPaymentData.bank_info.bank_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ชื่อบัญชี:</span>
                  <span className="font-medium">{mockPaymentData.bank_info.account_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">เลขบัญชี:</span>
                  <span className="font-mono font-medium">{mockPaymentData.bank_info.account_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PromptPay:</span>
                  <span className="font-mono font-medium">{mockPaymentData.bank_info.promptpay_id}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="font-semibold text-orange-900 mb-2">⚠️ ข้อสำคัญ</h4>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>• ระบุรหัสนักเรียน <strong>{mockPaymentData.student.student_code}</strong> ในหมายเหตุการโอน</li>
                <li>• อัพโหลดสลิปการโอนทันทีหลังชำระเงิน</li>
                <li>• ระบบจะตรวจสอบภายใน 2-4 ชั่วโมง (เวลาทำการ)</li>
              </ul>
            </div>
          </div>
        </DashboardCard>

        {/* Payment History */}
        <DashboardCard
          title="📊 ประวัติการชำระเงิน"
          className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
        >
          <div className="space-y-3">
            {mockPaymentData.payment_history.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl mb-2 block">📄</span>
                <p>ยังไม่มีประวัติการชำระเงิน</p>
              </div>
            ) : (
              <div className="space-y-3">
                {mockPaymentData.payment_history.map((payment) => (
                  <div key={payment.id} className="bg-white rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{payment.month}</h4>
                        <p className="text-sm text-gray-600">เลขที่: {payment.id}</p>
                        <p className="text-xs text-gray-500">
                          ชำระเมื่อ: {formatThaiDate(payment.paid_date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {formatThaiBaht(payment.amount)}
                        </p>
                        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ ชำระแล้ว
                        </div>
                        {payment.reference && (
                          <p className="text-xs text-gray-500 mt-1">
                            Ref: {payment.reference}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DashboardCard>
      </div>
      
      {/* Bottom Navigation */}
      <TabNavigation />

    </div>
  )
}